import React from 'react';

/**
 * QosPanel — live RTCP / WebRTC media stats sidecar to the softphone.
 *
 * Reads the same `callQuality` object that useSIP already computes every 2s
 * via getStats():
 *   { packetsLost, packetsReceived, packetsSent, bytesReceived, bytesSent,
 *     jitter (ms), roundTripTime (ms), mos }
 *
 * Renders next to the softphone (parent decides layout). Has its own
 * collapse button — when collapsed, the card shrinks to a thin vertical
 * strip so the active call stays visible without taking up real estate.
 *
 * Collapsing is purely visual; the call and the underlying stats poll keep
 * running.
 */
function lossRate(q) {
  if (!q) return 0;
  const total = (q.packetsLost || 0) + (q.packetsReceived || 0);
  return total > 0 ? (q.packetsLost / total) * 100 : 0;
}

function mosBucket(mosStr) {
  const m = parseFloat(mosStr);
  if (isNaN(m)) return { label: '—', color: '#9CA3AF' };
  if (m >= 4.0) return { label: 'Excellent', color: '#3BD162' };
  if (m >= 3.5) return { label: 'Good',      color: '#5AB6FF' };
  if (m >= 3.0) return { label: 'Fair',      color: '#F5A623' };
  if (m >= 2.5) return { label: 'Poor',      color: '#E66F00' };
  return         { label: 'Bad',       color: '#E53935' };
}

function StatRow({ label, value, unit, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      padding: '6px 0', borderBottom: '1px dashed var(--cx-border, #e4e7eb)',
    }}>
      <span style={{ fontSize: 11, color: 'var(--cx-muted, #6B7280)' }}>{label}</span>
      <span style={{
        fontFamily: 'monospace',
        fontSize: 13,
        fontWeight: 600,
        color: color || 'var(--cx-text, #1a202c)',
      }}>
        {value}
        {unit && <span style={{ fontWeight: 400, marginLeft: 2, fontSize: 11, opacity: 0.7 }}>{unit}</span>}
      </span>
    </div>
  );
}

export default function QosPanel({ callQuality, callActive, collapsed, onToggleCollapse, onClose }) {
  const loss = lossRate(callQuality);
  const mos  = mosBucket(callQuality?.mos);

  // Collapsed strip — just a vertical "QoS" badge with the MOS color dot.
  if (collapsed) {
    return (
      <div
        title="Click to expand QoS panel"
        onClick={onToggleCollapse}
        style={{
          width: 36,
          minWidth: 36,
          maxWidth: 36,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingTop: 12,
          gap: 8,
          background: 'var(--cx-panel, #fff)',
          border: '1px solid var(--cx-border, #e4e7eb)',
          borderRadius: 8,
          cursor: 'pointer',
          boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
        }}
      >
        <div style={{
          writingMode: 'vertical-rl', transform: 'rotate(180deg)',
          fontSize: 11, fontWeight: 600, letterSpacing: 1,
          color: 'var(--cx-text, #1a202c)',
        }}>
          ▸ QoS
        </div>
        <div style={{
          width: 10, height: 10, borderRadius: 999, background: mos.color,
        }} title={callActive ? `MOS ${callQuality?.mos || '—'} (${mos.label})` : 'No active call'} />
      </div>
    );
  }

  return (
    <div style={{
      flex: '0 0 260px',
      width: 260,
      maxWidth: 260,
      alignSelf: 'flex-start',
      maxHeight: 'calc(100vh - 24px)',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--cx-panel, #fff)',
      border: '1px solid var(--cx-border, #e4e7eb)',
      borderRadius: 8,
      boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '8px 10px',
        borderBottom: '1px solid var(--cx-border, #e4e7eb)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'var(--cx-subtle, #f8fafc)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>📶 QoS</span>
          {callActive && (
            <span style={{
              padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 600,
              background: mos.color, color: '#fff',
            }}>
              {mos.label}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={onToggleCollapse}
            title="Collapse"
            style={{
              border: '1px solid var(--cx-border, #e4e7eb)',
              background: 'transparent',
              padding: '2px 6px',
              fontSize: 11,
              borderRadius: 4,
              cursor: 'pointer',
              color: 'var(--cx-text, #1a202c)',
            }}
          >
            ◂ Collapse
          </button>
          {onClose && (
            <button
              onClick={onClose}
              title="Close"
              style={{
                border: '1px solid var(--cx-border, #e4e7eb)',
                background: 'transparent',
                padding: '2px 6px',
                fontSize: 11,
                borderRadius: 4,
                cursor: 'pointer',
                color: 'var(--cx-text, #1a202c)',
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '8px 10px' }}>
        {!callActive ? (
          <div style={{ fontSize: 11, color: 'var(--cx-muted, #6B7280)', padding: '12px 4px', textAlign: 'center' }}>
            No active call.<br/>Stats appear during a call.
          </div>
        ) : !callQuality ? (
          <div style={{ fontSize: 11, color: 'var(--cx-muted, #6B7280)', padding: '12px 4px', textAlign: 'center' }}>
            Waiting for first sample…
          </div>
        ) : (
          <>
            <StatRow
              label="MOS"
              value={callQuality.mos || '—'}
              color={mos.color}
            />
            <StatRow
              label="Jitter"
              value={callQuality.jitter ?? '—'}
              unit="ms"
              color={callQuality.jitter > 30 ? '#E66F00' : undefined}
            />
            <StatRow
              label="RTT"
              value={callQuality.roundTripTime ?? '—'}
              unit="ms"
              color={callQuality.roundTripTime > 200 ? '#E66F00' : undefined}
            />
            <StatRow
              label="Packet loss"
              value={loss.toFixed(2)}
              unit="%"
              color={loss > 2 ? '#E53935' : loss > 0.5 ? '#F5A623' : undefined}
            />
            <StatRow
              label="Packets sent"
              value={(callQuality.packetsSent || 0).toLocaleString()}
            />
            <StatRow
              label="Packets rcvd"
              value={(callQuality.packetsReceived || 0).toLocaleString()}
            />
            <StatRow
              label="Packets lost"
              value={(callQuality.packetsLost || 0).toLocaleString()}
              color={callQuality.packetsLost > 0 ? '#E66F00' : undefined}
            />
            <div style={{
              marginTop: 8,
              fontSize: 10,
              color: 'var(--cx-muted, #6B7280)',
              fontStyle: 'italic',
            }}>
              Sampled every 2 s from WebRTC getStats(). MOS estimated from RTT + jitter + loss (E-model).
            </div>
          </>
        )}
      </div>
    </div>
  );
}
